import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { KanoCategoryType } from '../../ai/useKanoPrediction';
import { FeatureModel } from '../../interfaces/feature';
import { typography } from '../../theme/typography';

export interface KanoResultBoxProps {
  type: KanoCategoryType;
  features: FeatureModel[];
  containerWidth: number;
  containerHeight: number;
}

export const KanoResultBox: React.FC<KanoResultBoxProps> = ({ type, features, containerHeight, containerWidth }) => {
  const padding = 10;
  const xLabelWidth = 120;
  const yLabelHeight = 30;
  const yOutsidePadding = 40;

  const [boxWidth, setBoxWidth] = useState<number>(0);
  const [boxHeight, setBoxHeight] = useState<number>(0);
  const [smallBoxHeight, setSmallBoxHeight] = useState<number>(0);
  const [indicators, setIndicators] = useState<{ top: string; left: string; caption: string }[]>([]);

  useEffect(() => {
    setBoxWidth(containerWidth / 2);
    setBoxHeight((containerHeight - 6 * padding + 2 * yOutsidePadding + 2 * yLabelHeight) * 0.3);
    setSmallBoxHeight((containerHeight - 6 * padding + 2 * yOutsidePadding + 2 * yLabelHeight) * 0.3 * 0.75);
  }, [containerWidth, containerHeight]);

  const getRandomPostion = (min: number, max: number): { top: string; left: string } => {
    return {
      top: `${Math.floor(Math.random() * (max - min + 1) + min)}%`,
      left: `${Math.floor(Math.random() * (max - min + 1) + min)}%`,
    };
  };

  useEffect(() => {
    const indicatorPositionRange = getIndicatorPositionRange();
    setIndicators(
      features.map((feature, index) => ({
        ...getRandomPostion(indicatorPositionRange.min, indicatorPositionRange.max),
        caption: feature.name,
      })),
    );
  }, [features]);

  const getCaption = (): string => {
    switch (type) {
      case 'Attractive':
        return 'Begeisterung';
      case 'Performance':
        return 'Leistung';
      case 'Indifferent':
        return 'Unerheblich';
      case 'Reverse':
        return 'Rückweisung';
      case 'Must-Be':
        return 'Basis';
      default:
        return '';
    }
  };

  const getColor = (): string => {
    switch (type) {
      case 'Attractive':
        return '#d3e3f4';
      case 'Performance':
        return '#cce5e4';
      case 'Indifferent':
        return '#dcdbe1';
      case 'Reverse':
        return '#f0c8db';
      case 'Must-Be':
        return '#f7edcb';
      default:
        return '';
    }
  };

  const getBoxPosition = (): { $top?: number; $bottom?: number; $left?: number; $right?: number } => {
    switch (type) {
      case 'Attractive':
        return { $left: xLabelWidth + padding, $top: yLabelHeight + padding };
      case 'Performance':
        return { $left: xLabelWidth + 2 * padding + boxWidth, $top: yLabelHeight + padding };
      case 'Indifferent':
        return { $left: xLabelWidth + padding + boxWidth / 2, $top: yLabelHeight + padding + boxHeight + padding };
      case 'Reverse':
        return { $left: xLabelWidth + padding, $bottom: yLabelHeight + padding };
      case 'Must-Be':
        return { $left: xLabelWidth + 2 * padding + boxWidth, $bottom: yLabelHeight + padding };
      default:
        return {};
    }
  };

  const getCaptionPosition = (): { $top?: number; $bottom?: number; $left?: number; $right?: number } => {
    switch (type) {
      case 'Attractive':
        return { $top: padding, $left: padding };
      case 'Performance':
        return { $top: padding, $right: padding };
      case 'Indifferent':
        return { $top: padding, $right: padding };
      case 'Reverse':
        return { $bottom: padding, $left: padding };
      case 'Must-Be':
        return { $bottom: padding, $right: padding };
      default:
        return {};
    }
  };

  const getIndicatorPositionRange = (): { min: number; max: number } => {
    switch (type) {
      case 'Attractive':
        return { min: 25, max: 90 };
      case 'Performance':
        return { min: 25, max: 90 };
      case 'Indifferent':
        return { min: 30, max: 90 };
      case 'Reverse':
        return { min: 10, max: 75 };
      case 'Must-Be':
        return { min: 10, max: 75 };
      default:
        return { min: 0, max: 0 };
    }
  };

  const getHeight = () => {
    if (type === 'Indifferent') {
      return smallBoxHeight;
    }
    return boxHeight;
  };

  return (
    <Box $width={boxWidth - padding} $height={getHeight()} {...getBoxPosition()} $color={getColor()}>
      <Caption {...getCaptionPosition()}>{getCaption()}</Caption>
      {indicators.map((indicator, index) => (
        <Indicator key={index} $top={indicator.top} $left={indicator.left}>
          {indicator.caption}
        </Indicator>
      ))}
    </Box>
  );
};

const Box = styled.div<{
  $width?: number;
  $height?: number;
  $top?: number;
  $bottom?: number;
  $left?: number;
  $right?: number;
  $color?: string;
}>`
  position: absolute;
  background-color: ${({ $color }) => $color || '#b4b7cf'};
  border-radius: 10px;
  width: ${({ $width }) => $width || 20}px;
  height: ${({ $height }) => $height || 20}px;
  ${({ $top }) => ($top ? `top: ${$top}px;` : '')}
  ${({ $bottom }) => ($bottom ? `bottom: ${$bottom}px;` : '')}
  ${({ $left }) => ($left ? `left: ${$left}px;` : '')}
  ${({ $right }) => ($right ? `right: ${$right}px;` : '')}
`;

const Caption = styled.div<{ $top?: number; $left?: number; $right?: number; $bottom?: number }>`
  position: absolute;
  ${({ $top }) => ($top ? `top: ${$top}px;` : '')}
  ${({ $bottom }) => ($bottom ? `bottom: ${$bottom}px;` : '')}
  ${({ $left }) => ($left ? `left: ${$left}px;` : '')}
  ${({ $right }) => ($right ? `right: ${$right}px;` : '')}  
  ${typography('body1')};
`;

const Indicator = styled.div<{ $top?: string; $left?: string }>`
  position: absolute;
  top: ${({ $top }) => $top || 0};
  left: ${({ $left }) => $left || 0};
  ${typography('caption1')};
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    background-color: #6600e7;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    left: -26px;
    transform: translateY(-2px);
  }
`;
